import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, ArrowRight, ArrowLeft, Check, MapPin, Phone, User, CreditCard, Eye, EyeOff, ShieldCheck, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);
const steps = ["Account", "Address", "Port-in", "Payment"];
const SIGNUP_IMG = "https://images.unsplash.com/photo-1742462950369-50fcee27bfac?w=800&q=80&auto=format";

const stripeAppearance = {
  theme: "night",
  labels: "floating",
  variables: {
    colorPrimary: "#D4AF37",
    colorBackground: "#0F0F0F",
    colorText: "#F5F5F5",
    colorTextSecondary: "#A0A0A0",
    colorDanger: "#EF4444",
    borderRadius: "12px",
    fontFamily: "Manrope, sans-serif",
  },
  rules: {
    ".Input": { border: "1px solid #2A2A2A", backgroundColor: "#0F0F0F" },
    ".Input:focus": { border: "1px solid #D4AF37", boxShadow: "0 0 0 1px #D4AF37" },
    ".Tab": { border: "1px solid #2A2A2A", backgroundColor: "#0F0F0F" },
    ".Tab--selected": { border: "1px solid #D4AF37", backgroundColor: "#1A1A1A" },
    ".Label": { color: "#A0A0A0" },
  },
};

function EmbeddedPaymentForm({ form, userId, goBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo/validate`, { code: promoCode });
      setPromoResult(data);
    } catch { setPromoResult({ valid: false, message: "Error validating code." }); }
    finally { setPromoLoading(false); }
  };

  const activationWaived = promoResult?.valid && promoResult?.type === "activation_waiver";
  const totalDue = activationWaived ? 50.00 : 85.00;

  const handleSubmit = async () => {
    if (!stripe || !elements || !agreed) return;
    setLoading(true);
    setError("");

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: `${window.location.origin}/dashboard` },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message);
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/checkout/finalize`, {
        setup_intent_id: setupIntent.id,
        user_id: userId,
        promo_code: activationWaived ? promoResult.code : "",
      }, { withCredentials: true });
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Payment processing failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Order Summary */}
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-5">
        <h3 className="font-outfit font-semibold text-[#F5F5F5] mb-4">Order Summary</h3>
        <div className="space-y-2 mb-4">
          <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">Prestigious Wireless Unlimited</span><span className="text-[#F5F5F5] font-manrope">$50.00/mo</span></div>
          <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">One-Time Activation Fee</span>{activationWaived ? <span className="text-emerald-400 font-manrope line-through-none"><span className="line-through text-[#A0A0A0] mr-2">$35.00</span>$0.00</span> : <span className="text-[#F5F5F5] font-manrope">$35.00</span>}</div>
          {activationWaived && <div className="flex justify-between"><span className="text-emerald-400 font-manrope text-xs">Promo: {promoResult.code}</span><span className="text-emerald-400 font-manrope text-sm">-$35.00</span></div>}
        </div>
        <div className="border-t border-[#2A2A2A] pt-3 flex justify-between">
          <span className="font-outfit font-semibold text-[#F5F5F5]">Due Today</span>
          <span className="font-outfit font-bold text-[#D4AF37] text-xl" data-testid="total-due">${totalDue.toFixed(2)}</span>
        </div>
      </div>

      {/* Promo Code */}
      <div className="flex gap-2">
        <input value={promoCode} onChange={e => { setPromoCode(e.target.value); setPromoResult(null); }}
          className="flex-1 bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] rounded-xl px-4 py-2.5 text-[#F5F5F5] font-manrope text-sm outline-none uppercase" placeholder="Promo Code (Optional)" data-testid="promo-code-input" />
        <button onClick={validatePromo} disabled={promoLoading || !promoCode.trim()} className="px-4 py-2.5 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#A0A0A0] hover:text-[#D4AF37] rounded-xl text-sm font-semibold transition-all disabled:opacity-50" data-testid="apply-promo-btn">
          {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
        </button>
      </div>
      {promoResult && (
        <p className={`text-sm font-manrope -mt-2 ${promoResult.valid ? "text-emerald-400" : "text-red-400"}`} data-testid="promo-result">{promoResult.message}</p>
      )}

      {/* Review Info */}
      <div className="space-y-2">
        <div className="bg-[#1A1A1A] rounded-xl p-3 flex justify-between"><span className="text-[#A0A0A0] text-sm">Account</span><span className="text-[#F5F5F5] text-sm">{form.name} &middot; {form.email}</span></div>
        <div className="bg-[#1A1A1A] rounded-xl p-3 flex justify-between"><span className="text-[#A0A0A0] text-sm">Shipping</span><span className="text-[#F5F5F5] text-sm text-right">{form.city}, {form.state} {form.zip_code}</span></div>
      </div>

      {/* Payment Method Label */}
      <div className="flex items-center gap-3">
        <CreditCard className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-outfit text-lg font-semibold text-[#F5F5F5]">Payment Details</h2>
      </div>
      <p className="text-[#A0A0A0] text-xs font-manrope -mt-3">Bank Account (ACH) recommended for lower fees and reliable billing.</p>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm font-manrope" data-testid="payment-error">{error}</div>}

      {/* Stripe Payment Element */}
      <div data-testid="stripe-payment-element">
        <PaymentElement options={{
          wallets: { applePay: "auto", googlePay: "auto" },
          paymentMethodOrder: ["apple_pay", "google_pay", "card", "us_bank_account"],
        }} />
      </div>

      {/* Terms checkbox */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4AF37]" data-testid="terms-checkbox" />
        <span className="text-[#A0A0A0] text-sm font-manrope">I agree to the <Link to="/terms" target="_blank" className="text-[#D4AF37] underline">Terms & Conditions</Link> and understand that autopay is required.</span>
      </label>

      {/* Security badge */}
      <div className="flex items-center gap-2 justify-center text-[#A0A0A0]/60">
        <Lock className="w-3.5 h-3.5" />
        <span className="text-xs font-manrope">Secured by Stripe. Your data never touches our servers.</span>
        <ShieldCheck className="w-3.5 h-3.5" />
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button onClick={goBack} className="flex-1 border border-[#333] text-[#F5F5F5] py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 hover:border-[#D4AF37] transition-all" data-testid="payment-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={handleSubmit} disabled={loading || !agreed || !stripe}
          className="flex-1 bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all text-lg"
          data-testid="complete-payment-button">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : "Complete Payment"}
        </button>
      </div>
    </div>
  );
}

export default function Signup() {
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    address_line1: "", address_line2: "", city: "", state: "", zip_code: "",
    port_in: false, port_in_number: "", port_in_carrier: "", port_in_account: "", port_in_pin: ""
  });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inputCls = "w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-xl px-4 py-3 text-[#F5F5F5] font-manrope outline-none transition-all";

  const canNext = () => {
    if (step === 0) return form.name && form.email && form.password.length >= 8 && form.phone.length >= 10;
    if (step === 1) return form.address_line1 && form.city && form.state && form.zip_code;
    return true;
  };

  const goToPayment = async () => {
    setSetupLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/checkout/create-setup`, { ...form, origin_url: window.location.origin });
      setClientSecret(data.client_secret);
      setUserId(data.user_id);
      setStep(3);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed to initialize payment. Please try again.");
    } finally {
      setSetupLoading(false);
    }
  };

  // Step 3: Embedded payment form wrapped in Elements
  if (step === 3 && clientSecret) {
    return (
      <div className="min-h-screen bg-[#050505] px-6 py-12">
        <div className="max-w-lg mx-auto">
          <div className="relative rounded-2xl overflow-hidden mb-8 h-40">
            <img src={SIGNUP_IMG} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
            <div className="absolute bottom-4 left-6"><p className="font-outfit text-xl font-bold text-[#F5F5F5]">Prestigious Wireless</p><p className="text-[#A0A0A0] text-sm font-manrope">Complete Your Payment</p></div>
          </div>
          <div className="flex items-center gap-2 mb-8">
            {steps.map((st, i) => (<div key={i} className="flex-1"><div className={`h-1.5 rounded-full transition-all duration-500 ${i <= step ? "bg-[#D4AF37]" : "bg-[#2A2A2A]"}`} /><p className={`text-[10px] mt-1.5 font-manrope ${i <= step ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>{st}</p></div>))}
          </div>
          <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8" data-testid="embedded-payment-form">
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
              <EmbeddedPaymentForm form={form} userId={userId} goBack={() => setStep(2)} />
            </Elements>
          </div>
        </div>
      </div>
    );
  }

  // Steps 0-2
  return (
    <div className="min-h-screen bg-[#050505] px-6 py-12">
      <div className="max-w-lg mx-auto">
        <div className="relative rounded-2xl overflow-hidden mb-8 h-40">
          <img src={SIGNUP_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
          <div className="absolute bottom-4 left-6"><p className="font-outfit text-xl font-bold text-[#F5F5F5]">Prestigious Wireless</p><p className="text-[#A0A0A0] text-sm font-manrope">Sign up for Unlimited Service</p></div>
        </div>
        <div className="flex items-center gap-2 mb-8">
          {steps.map((st, i) => (<div key={i} className="flex-1"><div className={`h-1.5 rounded-full transition-all duration-500 ${i <= step ? "bg-[#D4AF37]" : "bg-[#2A2A2A]"}`} /><p className={`text-[10px] mt-1.5 font-manrope ${i <= step ? "text-[#D4AF37]" : "text-[#A0A0A0]"}`}>{st}</p></div>))}
        </div>
        <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8" data-testid="signup-form">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm mb-6" data-testid="signup-error">{error}</div>}

          {step === 0 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><User className="w-5 h-5 text-[#D4AF37]" /><h2 className="font-outfit text-xl font-semibold text-[#F5F5F5]">Your Account</h2></div>
              <div><label className="block text-[#A0A0A0] text-sm font-manrope mb-1.5">Full Name</label><input value={form.name} onChange={e => s("name", e.target.value)} className={inputCls} placeholder="John Doe" data-testid="signup-name" /></div>
              <div><label className="block text-[#A0A0A0] text-sm font-manrope mb-1.5">Email</label><input type="email" value={form.email} onChange={e => s("email", e.target.value)} className={inputCls} placeholder="you@example.com" data-testid="signup-email" /></div>
              <div><label className="block text-[#A0A0A0] text-sm font-manrope mb-1.5">Password</label>
                <div className="relative"><input type={showPw ? "text" : "password"} value={form.password} onChange={e => s("password", e.target.value)} className={`${inputCls} pr-12`} placeholder="Min 8 characters" data-testid="signup-password" /><button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0A0A0]">{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div>
              </div>
              <div><label className="block text-[#A0A0A0] text-sm font-manrope mb-1.5">Phone Number</label><input type="tel" value={form.phone} onChange={e => s("phone", e.target.value)} className={inputCls} placeholder="(555) 555-5555" data-testid="signup-phone" /></div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><MapPin className="w-5 h-5 text-[#D4AF37]" /><h2 className="font-outfit text-xl font-semibold text-[#F5F5F5]">Shipping Address</h2></div>
              <input value={form.address_line1} onChange={e => s("address_line1", e.target.value)} className={inputCls} placeholder="Street Address" data-testid="signup-address1" />
              <input value={form.address_line2} onChange={e => s("address_line2", e.target.value)} className={inputCls} placeholder="Apt, Suite (optional)" />
              <div className="grid grid-cols-3 gap-3">
                <input value={form.city} onChange={e => s("city", e.target.value)} className={inputCls} placeholder="City" data-testid="signup-city" />
                <input value={form.state} onChange={e => s("state", e.target.value)} className={inputCls} placeholder="State" data-testid="signup-state" />
                <input value={form.zip_code} onChange={e => s("zip_code", e.target.value)} className={inputCls} placeholder="ZIP" data-testid="signup-zip" />
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2"><Phone className="w-5 h-5 text-[#D4AF37]" /><h2 className="font-outfit text-xl font-semibold text-[#F5F5F5]">Port Your Number?</h2></div>
              <p className="text-[#A0A0A0] font-manrope text-sm">Keep your existing number by porting it to Prestigious Wireless.</p>
              <div className="flex gap-4">
                <button onClick={() => s("port_in", true)} className={`flex-1 py-3 rounded-xl font-semibold transition-all ${form.port_in ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#A0A0A0] border border-[#2A2A2A]"}`} data-testid="signup-portin-yes">Yes, Port Number</button>
                <button onClick={() => s("port_in", false)} className={`flex-1 py-3 rounded-xl font-semibold transition-all ${!form.port_in ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#A0A0A0] border border-[#2A2A2A]"}`} data-testid="signup-portin-no">New Number</button>
              </div>
              {form.port_in && (
                <div className="space-y-4 pt-2">
                  <input value={form.port_in_number} onChange={e => s("port_in_number", e.target.value)} className={inputCls} placeholder="Number to port" data-testid="signup-portin-number" />
                  <input value={form.port_in_carrier} onChange={e => s("port_in_carrier", e.target.value)} className={inputCls} placeholder="Current carrier" />
                  <input value={form.port_in_account} onChange={e => s("port_in_account", e.target.value)} className={inputCls} placeholder="Account number" />
                  <input value={form.port_in_pin} onChange={e => s("port_in_pin", e.target.value)} className={inputCls} placeholder="Account PIN" />
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-4 mt-8">
            {step > 0 && <button onClick={() => setStep(step - 1)} className="flex-1 border border-[#333] text-[#F5F5F5] py-3 rounded-full font-semibold flex items-center justify-center gap-2 hover:border-[#D4AF37] transition-all" data-testid="signup-back"><ArrowLeft className="w-4 h-4" /> Back</button>}
            {step < 2 ? (
              <button onClick={() => { setError(""); setStep(step + 1); }} disabled={!canNext()} className="flex-1 bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all" data-testid="signup-next">Next <ArrowRight className="w-4 h-4" /></button>
            ) : (
              <button onClick={goToPayment} disabled={setupLoading} className="flex-1 bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-all" data-testid="signup-to-payment">
                {setupLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing...</> : <><CreditCard className="w-4 h-4" /> Continue to Payment</>}
              </button>
            )}
          </div>
        </div>
        <p className="text-center text-[#A0A0A0] text-sm font-manrope mt-6">Already have an account? <Link to="/login" className="text-[#D4AF37] font-semibold" data-testid="signup-login-link">Sign in</Link></p>
      </div>
    </div>
  );
}
